import { lazy } from "react";
import { AppRouteConfig } from "./types";
import { adminRoutes, adminNavigationGroups } from "./adminRoutes";
import { guestRoutes, publicGuestRoutes } from "./guestRoutes";

// Lazy load layout components
const AdminLayout = lazy(() => import("../../components/admin/AdminLayout"));
const ProtectedRoute = lazy(() => import("../../components/ProtectedRoute"));

/**
 * Complete application route configuration
 * This is the single source of truth for all routing in the application
 */
export const routeConfig: AppRouteConfig = {
  admin: {
    basePath: "/admin",
    layout: AdminLayout,
    guard: ProtectedRoute,
    routes: adminRoutes,
    requiresAuth: true,
    defaultPath: "dashboard",
  },
  guest: {
    basePath: "/g/:token",
    routes: guestRoutes,
    requiresAuth: false,
  },
  public: {
    basePath: "/",
    routes: publicGuestRoutes,
    requiresAuth: false,
  },
};

// Re-export utilities
export * from "./routeUtils";
export * from "./types";
export { adminNavigationGroups };

/**
 * Get all routes for a section
 */
export function getRoutes(section: "admin" | "guest" | "public") {
  return routeConfig[section]?.routes || [];
}

/**
 * Get route configuration for a specific path
 */
export function getRouteConfig(path: string) {
  // Check admin routes
  if (path.startsWith("/admin")) {
    const route = adminRoutes.find((r) => `/admin/${r.path}` === path);
    if (route) return route;
  }

  // Check guest routes
  if (path.includes("/g/")) {
    const guestPath = path.split("/").pop();
    const route = guestRoutes.find((r) => r.path === guestPath);
    if (route) return route;
  }

  // Check public routes
  const publicRoute = publicGuestRoutes.find((r) => r.path === path);
  if (publicRoute) return publicRoute;

  return null;
}
